/**
 * /underwriting — confidential AFM™ underwriting portal for warehouse
 * lenders, secondary-market buyers, and capital-markets reviewers.
 *
 * 🔑 SERVER-SIDE ACCESS (Jesse, 2026-10-21). The shared password (PasswordGate, with a fallback value in the bundle)
 * is gone from the bundle. The page renders only for a viewer the API authorizes — staff, partners with an approved
 * agreement, approved DoorlyMarket investors (backend/middleware/guideAccess) — and the guide's text, the matrix and
 * the PDF come from authenticated endpoints. Anyone else sees the original password page (Jesse, 2026-10-22), whose
 * password the server checks and answers with a 12-hour guide pass (About and Projections stay staff / NDA-signed
 * investors). Helmet sets noindex,nofollow so this page never appears in search.
 *
 * Five tabs:
 *   1. Guidelines     — full UWGuideContent (shared with admin + investor)
 *   2. Program Matrix — AFM tier table + req cards +
 *                        rate simulator (RatePricingSimulator shared)
 *   3. About Doorly   — philosophy, dual-tx model, leadership, tech stack
 *   4. Projections    — 3-yr origination forecast + balance sheet +
 *                        capital structure + secondary market
 *   5. Resources      — download center + compliance + contact
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import UWGuideBody, { useGuideDocument, useGuideSections, usePortalConfidential, resetGuideCache, NdaPrompt, derogRuleText, useUWMatrix, pctOf, usdOf, GuideVersionLine } from '../components/UWGuideContent';
import { openAfmPdf, unlockGuide } from '../utils/guideAuth';
import RatePricingSimulator from '../components/RatePricingSimulator';
import { Icon } from '../utils/icons';
// 🔑 WHERE DOORLY MAY ORIGINATE IS READ, NOT TYPED. This page claimed "TX and FL"
// in one card while its own subtitle and licence table said Florida was pending —
// and constants/marketExpansion.js is the mirror that backend/test/
// frontendMirrors.test.js holds against the backend licence list. The helpers
// render Florida out of the sentence by themselves the day the licence issues.
import { licensedStatesShort, pendingLicenseNote, LICENSES } from '../constants/marketExpansion';
import { SAMPLE_TAPE_PUBLISHED, SAMPLE_TAPE_PATH, SAMPLE_LOAN_TAPE } from '../constants/sampleTape';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

const PRIMARY = '#3a6ad9';
const NEAR_BLACK = '#111111';
const DANGER = '#ff082f';

const TABS = [
  { id: 'guidelines',  label: 'Guidelines' },
  { id: 'matrix',      label: 'Program Matrix' },
  { id: 'about',       label: 'About Doorly' },
  { id: 'projections', label: 'Projections' },
  { id: 'resources',   label: 'Resources' },
];

export default function UnderwritingPortal() {
  return (
    <>
      <Helmet>
        <title>Doorly | Underwriting Portal</title>
        <meta name="description" content="Doorly AFM™ underwriting guidelines, program matrix, and origination materials for warehouse lenders and secondary market investors." />
        <meta property="og:title" content="Doorly | Underwriting Portal" />
        <meta property="og:description" content="AFM™ program guidelines, matrices, and investor reference materials." />
        <meta property="og:url" content="https://app.godoorly.com/underwriting" />
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <PortalAccess>
        <UnderwritingPortalContent />
      </PortalAccess>
    </>
  );
}

// 🔑 THE ORIGINAL PASSWORD PAGE, CHECKED ON THE SERVER (Jesse, 2026-10-22: "remove the gate page … and bring back the
// original password page"). The look, wording and ?key= link of the old PasswordGate are back; the password goes to
// POST /api/uw-matrix/unlock and is compared there (UW_GUIDE_PASSWORD), never in the bundle. A signed-in staff member,
// partner or investor the API already authorizes goes straight in, as before. An approved investor who has not signed
// the NDA still gets the NDA prompt.
function PortalAccess({ children }) {
  const [gen, setGen] = useState(0);
  const unlocked = () => { resetGuideCache(); setGen((g) => g + 1); };
  return <PortalAccessInner key={gen} onUnlocked={unlocked}>{children}</PortalAccessInner>;
}

function PortalAccessInner({ children, onUnlocked }) {
  const { status, code } = useGuideDocument();
  if (status === 200) return children;
  if (status == null) {
    return (
      <div data-testid="uw-portal-checking" style={{ minHeight: '100vh', background: '#f9fafb', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px 16px', boxSizing: 'border-box' }}>
        <p style={{ fontSize: 14, color: '#6b7280' }}>Loading…</p>
      </div>
    );
  }
  if (code === 'NDA_REQUIRED') {
    return (
      <div data-testid="uw-portal-locked" style={{ minHeight: '100vh', background: '#f9fafb', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px 16px', boxSizing: 'border-box' }}>
        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 16, padding: '28px 28px 22px', maxWidth: 520, width: '100%', boxSizing: 'border-box' }}>
          <NdaPrompt />
        </div>
      </div>
    );
  }
  return <PortalPassword onUnlocked={onUnlocked} />;
}

// The original /underwriting PasswordGate screen (removed in 54d48c70), unchanged in look and wording.
function PortalPassword({ onUnlocked }) {
  const [pw, setPw] = useState('');
  const [error, setError] = useState('');
  const [shake, setShake] = useState(false);
  const [busy, setBusy] = useState(false);

  const attempt = async (value, { fromLink = false } = {}) => {
    setBusy(true);
    const r = await unlockGuide(value);
    setBusy(false);
    if (r === 'ok') { onUnlocked(); return; }
    if (fromLink) return;   // a stale ?key= link just shows the page
    setError(r.error);
    setShake(true);
    setTimeout(() => setShake(false), 450);
  };

  // ?key= link pre-fill: checked by the server, and stripped off the address bar either way so it never lingers in
  // screenshots or history.
  useEffect(() => {
    const key = new URLSearchParams(window.location.search).get('key');
    if (!key) return;
    window.history.replaceState({}, '', window.location.pathname);
    attempt(key, { fromLink: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!busy && pw) attempt(pw);
  };

  return (
    <div data-testid="uw-portal-locked" style={{
      minHeight: '100vh', backgroundColor: '#f9fafb',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: '24px 16px', fontFamily: 'inherit',
      boxSizing: 'border-box', maxWidth: '100%', overflowX: 'hidden',
    }}>
      <style>{`
        @keyframes uw-shake {
          0%, 100% { transform: translateX(0); }
          15% { transform: translateX(-6px); }
          30% { transform: translateX(6px); }
          45% { transform: translateX(-4px); }
          60% { transform: translateX(4px); }
          75% { transform: translateX(-2px); }
        }
      `}</style>
      <form data-testid="uw-password-form" onSubmit={handleSubmit} style={{
        background: '#fff', border: '1px solid #e5e7eb', borderRadius: 16,
        padding: '36px 28px', maxWidth: 420, width: '100%',
        boxShadow: '0 6px 24px rgba(0,0,0,0.06)',
        textAlign: 'center', boxSizing: 'border-box',
      }}>
        <img src="/doorly-market-logo.png" alt="Doorly"
          style={{ height: 40, width: 'auto', margin: '0 auto 22px', display: 'block' }} />

        <h1 style={{ margin: '0 0 6px', fontSize: 20, fontWeight: 800, color: NEAR_BLACK }}>
          Doorly Underwriting Portal
        </h1>
        <p style={{ margin: '0 0 22px', fontSize: 12, color: '#6b7280', letterSpacing: 0.04, textTransform: 'uppercase', fontWeight: 700 }}>
          Confidential — For Authorized Recipients Only
        </p>

        {/* No autoFocus: on a phone it pops the keyboard and scrolls the page mid-way (the original gate's note). */}
        <input
          type="password"
          data-testid="uw-password"
          value={pw}
          onChange={(e) => { setPw(e.target.value); if (error) setError(''); }}
          placeholder="Enter access password"
          autoComplete="current-password"
          style={{
            width: '100%', boxSizing: 'border-box',
            padding: '12px 14px', borderRadius: 10,
            border: `1px solid ${error ? DANGER : '#e5e7eb'}`,
            fontSize: 14, color: NEAR_BLACK, outline: 'none',
            marginBottom: 12,
            animation: shake ? 'uw-shake 0.45s ease' : 'none',
          }}
          onFocus={(e) => { if (!error) e.target.style.borderColor = PRIMARY; }}
          onBlur={(e) => { if (!error) e.target.style.borderColor = '#e5e7eb'; }}
        />

        {error && (
          <div data-testid="uw-password-error" style={{ fontSize: 12, color: DANGER, marginBottom: 12, fontWeight: 600, textAlign: 'left' }}>
            {error}
          </div>
        )}

        <button type="submit" disabled={busy} style={{
          width: '100%', padding: '12px 16px',
          background: PRIMARY, color: '#fff', border: 'none',
          borderRadius: 10, fontSize: 14, fontWeight: 700, cursor: busy ? 'default' : 'pointer', opacity: busy ? 0.7 : 1,
        }}>
          {busy ? 'Checking…' : 'Access Portal →'}
        </button>

        <div style={{ marginTop: 22, fontSize: 11, color: '#9ca3af', lineHeight: 1.6 }}>
          Go Doorly, LLC | NMLS #2764749 | Equal Housing Opportunity
        </div>
      </form>
    </div>
  );
}

function UnderwritingPortalContent() {
  const [tab, setTab] = useState('guidelines');

  return (
    <div style={{ minHeight: '100vh', background: '#f9fafb', fontFamily: 'inherit', maxWidth: '100%', overflowX: 'hidden', boxSizing: 'border-box' }}>
      <Header />
      <TabBar tab={tab} onChange={setTab} />

      <main style={{ maxWidth: 1180, margin: '0 auto', padding: '24px 16px 48px', boxSizing: 'border-box' }}>
        {tab === 'guidelines'  && <GuidelinesTab />}
        {tab === 'matrix'      && <MatrixTab />}
        {tab === 'about'       && <AboutTab />}
        {tab === 'projections' && <ProjectionsTab />}
        {tab === 'resources'   && <ResourcesTab />}
      </main>

      <Footer />
    </div>
  );
}

// ─── Header ──────────────────────────────────────────────────────────
function Header() {
  return (
    <div style={{ background: '#fff', borderBottom: '1px solid #e5e7eb' }}>
      <div style={{ maxWidth: 1180, margin: '0 auto', padding: '20px 16px 16px', boxSizing: 'border-box' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
          <Link to="/market" style={{ display: 'flex', alignItems: 'center', textDecoration: 'none' }}
            title="Visit DoorlyMarket">
            <img src="/doorly-market-logo.png" alt="DoorlyMarket" style={{ height: 32, width: 'auto', display: 'block', maxWidth: '100%' }} />
          </Link>
          <span style={{
            display: 'inline-block',
            background: '#fef2f2', color: DANGER,
            border: `1px solid ${DANGER}`, borderRadius: 20,
            padding: '4px 12px', fontSize: 12, fontWeight: 700,
            letterSpacing: 0.04, textTransform: 'uppercase',
          }}>
            Confidential
          </span>
        </div>
        <h1 style={{ margin: '14px 0 4px', fontSize: 'clamp(20px, 5vw, 26px)', fontWeight: 800, color: NEAR_BLACK, lineHeight: 1.2 }}>
          Doorly Underwriting Portal
        </h1>
        <p style={{ margin: 0, fontSize: 'clamp(13px, 3.5vw, 14px)', color: '#6b7280', lineHeight: 1.5 }}>
          AFM<sup style={{ fontSize: '0.55em', verticalAlign: 'super' }}>™</sup> Program Guidelines &amp; Investor Reference Materials
        </p>
      </div>
    </div>
  );
}

function TabBar({ tab, onChange }) {
  // Track whether the horizontal scroller has content hidden off either
  // side. Drives the fade-gradient overlays + the right-edge chevron so
  // mobile users (and any narrow viewport where the 5 tabs overflow)
  // get an obvious "there's more this way" affordance. Recomputed on
  // scroll + resize + first paint. We tolerate a 2px slack on each end
  // to account for sub-pixel rounding so the indicators don't flicker
  // when scrolled exactly to the start/end.
  const scrollerRef = useRef(null);
  const [overflow, setOverflow] = useState({ left: false, right: false });

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const update = () => {
      const maxScroll = el.scrollWidth - el.clientWidth;
      setOverflow({
        left: el.scrollLeft > 2,
        right: el.scrollLeft < maxScroll - 2,
      });
    };
    update();
    el.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    // Recompute once on next frame too — handles fonts loading late
    // and changing the measured scrollWidth after first paint.
    const rafId = window.requestAnimationFrame(update);
    return () => {
      el.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
      window.cancelAnimationFrame(rafId);
    };
  }, []);

  const Chevron = ({ direction }) => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
      style={{ display: 'block' }} aria-hidden="true">
      <path
        d={direction === 'right' ? 'M9 6l6 6-6 6' : 'M15 6l-6 6 6 6'}
        stroke={PRIMARY} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );

  return (
    <div style={{ background: '#fff', borderBottom: '1px solid #e5e7eb', position: 'sticky', top: 0, zIndex: 10 }}>
      <div style={{ maxWidth: 1180, margin: '0 auto', padding: '0 16px', boxSizing: 'border-box', position: 'relative' }}>
        <div ref={scrollerRef} className="tab-bar-scroll"
          style={{ display: 'flex', gap: 8, overflowX: 'auto', WebkitOverflowScrolling: 'touch', scrollBehavior: 'smooth' }}>
          {TABS.map((t) => {
            const active = t.id === tab;
            return (
              <button key={t.id} onClick={() => onChange(t.id)}
                style={{
                  background: 'none', border: 'none', cursor: 'pointer',
                  padding: '14px 14px 12px',
                  fontSize: 14, fontWeight: active ? 700 : 500,
                  color: active ? PRIMARY : '#6b7280',
                  borderBottom: `3px solid ${active ? PRIMARY : 'transparent'}`,
                  whiteSpace: 'nowrap', flexShrink: 0,
                }}>
                {t.label}
              </button>
            );
          })}
        </div>

        {/* Left fade — appears once the borrower has scrolled past the
            first tab. Pointer-events: none so it doesn't swallow taps
            on the tabs underneath. */}
        {overflow.left && (
          <div aria-hidden="true" style={{
            position: 'absolute', top: 0, bottom: 0, left: 16, width: 36,
            background: 'linear-gradient(to right, #fff, rgba(255,255,255,0))',
            pointerEvents: 'none',
          }} />
        )}

        {/* Right fade + chevron — the "there's more this way" affordance.
            The chevron is a separate button (over the fade) that scrolls
            the strip ~70% of the visible width on tap, so users with no
            scroll gesture (e.g. desktop without a trackpad) can still
            reach the hidden tabs. */}
        {overflow.right && (
          <>
            <div aria-hidden="true" style={{
              position: 'absolute', top: 0, bottom: 0, right: 16, width: 48,
              background: 'linear-gradient(to left, #fff 30%, rgba(255,255,255,0))',
              pointerEvents: 'none',
            }} />
            <button
              type="button"
              aria-label="Scroll tabs right"
              onClick={() => {
                const el = scrollerRef.current;
                if (!el) return;
                el.scrollBy({ left: el.clientWidth * 0.7, behavior: 'smooth' });
              }}
              style={{
                position: 'absolute', top: '50%', right: 18,
                transform: 'translateY(-50%)',
                background: '#fff', border: '1px solid #e5e7eb',
                borderRadius: 999, width: 28, height: 28,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                cursor: 'pointer', padding: 0,
                boxShadow: '0 1px 3px rgba(15,23,42,0.08)',
              }}>
              <Chevron direction="right" />
            </button>
          </>
        )}
      </div>
    </div>
  );
}

function Footer() {
  return (
    <div style={{ background: '#fff', borderTop: '1px solid #e5e7eb', padding: '24px 16px' }}>
      <div style={{ maxWidth: 1180, margin: '0 auto', textAlign: 'center', fontSize: 11, color: '#9ca3af', lineHeight: 1.7 }}>
        <div style={{ fontWeight: 600, color: '#374151' }}>
          Go Doorly, LLC | NMLS #2764749 | Equal Housing Opportunity | 8708 S Congress Ave, Ste E500-202, Austin, TX 78745
        </div>
        <div style={{ marginTop: 6 }}>
          This document is confidential and intended solely for authorized recipients. Not for distribution.
        </div>
      </div>
    </div>
  );
}

// ─── Tab 1: Guidelines ───────────────────────────────────────────────
function GuidelinesTab() {
  const sections = useGuideSections();
  const [search, setSearch] = useState('');
  const [matchCount, setMatchCount] = useState(0);
  const contentRef = useRef(null);

  const highlightSearch = useMemo(() => () => {
    const root = contentRef.current;
    if (!root) return;
    root.querySelectorAll('mark[data-uw-search]').forEach((m) => {
      const parent = m.parentNode;
      while (m.firstChild) parent.insertBefore(m.firstChild, m);
      parent.removeChild(m);
      parent.normalize();
    });
    if (!search.trim()) { setMatchCount(0); return; }
    const term = search.trim();
    const re = new RegExp(term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'gi');
    let count = 0;
    let firstMatch = null;
    const walk = (node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        const text = node.nodeValue;
        if (!text || !re.test(text)) return;
        re.lastIndex = 0;
        const frag = document.createDocumentFragment();
        let lastIdx = 0;
        let m;
        while ((m = re.exec(text)) !== null) {
          if (m.index > lastIdx) frag.appendChild(document.createTextNode(text.slice(lastIdx, m.index)));
          const mark = document.createElement('mark');
          mark.setAttribute('data-uw-search', '1');
          mark.style.background = '#fffd5b';
          mark.style.color = NEAR_BLACK;
          mark.style.padding = '0 2px';
          mark.style.borderRadius = '3px';
          mark.appendChild(document.createTextNode(m[0]));
          frag.appendChild(mark);
          lastIdx = m.index + m[0].length;
          count++;
          if (!firstMatch) firstMatch = mark;
        }
        if (lastIdx < text.length) frag.appendChild(document.createTextNode(text.slice(lastIdx)));
        node.parentNode.replaceChild(frag, node);
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        if (['SCRIPT','STYLE','MARK','INPUT','BUTTON','TEXTAREA'].includes(node.tagName)) return;
        Array.from(node.childNodes).forEach(walk);
      }
    };
    walk(root);
    setMatchCount(count);
    if (firstMatch) firstMatch.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [search]);

  useEffect(() => {
    const t = setTimeout(highlightSearch, 80);
    return () => clearTimeout(t);
  }, [highlightSearch]);

  return (
    <div>
      <SectionHeader>Underwriting Guidelines</SectionHeader>

      {/* Top anchor strip — quick jumps to each section */}
      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: '10px 12px', marginBottom: 12, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        {sections.map((s, i) => (
          <a key={s.id} href={`#${s.id}`}
            style={{ fontSize: 12, color: '#374151', textDecoration: 'none', padding: '4px 10px', borderRadius: 6, background: '#f9fafb', border: '1px solid #f3f4f6' }}
            onMouseEnter={(e) => (e.currentTarget.style.background = '#eff3fe')}
            onMouseLeave={(e) => (e.currentTarget.style.background = '#f9fafb')}>
            <span style={{ color: '#9ca3af', marginRight: 6, fontVariantNumeric: 'tabular-nums' }}>{String(i + 1).padStart(2, '0')}</span>
            {s.label}
          </a>
        ))}
      </div>

      {/* Search bar */}
      <div style={{ position: 'sticky', top: 56, zIndex: 5, background: '#f9fafb', paddingBottom: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: '6px 10px' }}>
          <Icon name="search" size={16} color="#9ca3af" />
          <input type="text" placeholder="Search guidelines… (FICO, DTI, reserves, seasoning, etc.)"
            value={search} onChange={(e) => setSearch(e.target.value)}
            style={{ flex: 1, border: 'none', outline: 'none', fontSize: 14, color: '#111827', height: 32 }} />
          {search && (
            <span style={{ fontSize: 12, color: '#6b7280', whiteSpace: 'nowrap' }}>
              {matchCount === 0 ? 'No matches' : `${matchCount} match${matchCount === 1 ? '' : 'es'}`}
            </span>
          )}
          {search && (
            <button onClick={() => setSearch('')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280', fontSize: 16 }}>✕</button>
          )}
        </div>
      </div>

      <div ref={contentRef} style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: '24px 28px' }}>
        <UWGuideBody allowCopyLinks={false} />
      </div>
    </div>
  );
}

// ─── Tab 2: Program Matrix ───────────────────────────────────────────
// ⚠️ THE HARDCODED MATRIX IS GONE, AND THE REASON IS WORTH KEEPING.
// This tab carried its own copy of the tier table, the seasoning table, the
// alt-doc caps and the reserve overlays, hand-maintained and stamped
// "Last Updated: May 2025". The Guidelines tab beside it carried a different
// copy. They disagreed in public, on the same page, on material credit terms:
//
//   Chapter 7 seasoning   matrix 1 year   vs   guidelines 4 years
//   Foreclosure           matrix 3 years  vs   guidelines 4 years
//   Deed-in-lieu/short    matrix 2 years  vs   guidelines 3 years
//   Minimum FICO          matrix 560      vs   engine + guidelines 550
//
// A prospective credit facility reviewing /underwriting found it. Two documents
// on one page contradicting each other is worse than either being wrong alone —
// it says we do not know which one we originate to.
//
// Both tabs now render the SAME payload from constants/afmMatrix.js, which is
// also what utils/creditAnalyzer.js auto-declines against. The occupancy /
// transaction / property-type columns are constant across every tier and are
// the only values still stated here.
// 🔑 AFM v2 (2026-10-21): every value on this tab is the payload's (GET /api/uw-matrix). The fixed columns too —
// the property types are the matrix's list, not a typed "SFR, PUD, Condos".
const MATRIX_HEADERS = ['Occupancy', 'Transaction', 'Property Type', 'Loan Amount', 'Max LTV', 'Max DTI', 'Base Reserves', 'Min Credit Score'];
const matrixFixedCols = (matrix) => ['Primary', 'Purchase', ((matrix && matrix.propertyTypes && matrix.propertyTypes.eligible) || []).join(', ')];
const matrixRowsFrom = (tiers, matrix) => tiers.map((t) => [...matrixFixedCols(matrix), t.loan, t.ltv, t.dti, t.reserves, t.fico]);

function MatrixTab() {
  const matrix = useUWMatrix();
  if (!matrix) {
    return (
      <div>
        <SectionHeader>AFM™ Program Matrix</SectionHeader>
        <p style={{ fontSize: 14, color: '#6b7280' }}>Loading the program matrix…</p>
      </div>
    );
  }
  const tiers = matrix?.tiers || [];
  const income = matrix?.income;
  const itin = matrix?.itin;
  const rentFree = matrix?.rentFreeFirstTimeBuyer;
  const factorWord = (n) => (n === 1 ? 'one compensating factor' : `${n} compensating factors`);
  return (
    <div>
      <SectionHeader>AFM™ Program Matrix</SectionHeader>
      {/* ⚠️ VERSION AND EFFECTIVE DATE, NOT "Last Updated: May 2025". */}
      <p style={{ margin: '0 0 6px', fontSize: 13, color: '#6b7280' }}>
        Owner-Occupied | 30-Year Fixed | Fully Amortized | {licensedStatesShort()}
        {pendingLicenseNote() ? ` (${pendingLicenseNote()})` : ''}
      </p>
      <GuideVersionLine matrix={matrix} style={{ margin: '0 0 18px' }} />

      <DataTable headers={MATRIX_HEADERS} rows={matrixRowsFrom(tiers, matrix)} />

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 280px), 1fr))', gap: 14, marginTop: 22 }}>
        <ReqCard title="General Requirements" rows={[
          // 🚨 WHERE DOORLY MAY ORIGINATE IS THE LICENCE MIRROR (marketExpansion) — never a typed list, never a target
          // date. AFM v2: rural properties are not eligible.
          ['Eligible States',          `${licensedStatesShort()} — rural properties are not eligible. ${pendingLicenseNote()}`.trim()],
          ['Eligible Borrowers',       `US citizens, permanent and eligible non-permanent residents, ITIN (max ${pctOf(itin.maxDTI)} DTI, ${itin.reserveOverlayMonths} mo reserves, ${itin.minOwnFundsPct}% own funds)`],
          ['Foreign Nationals',        'Ineligible'],
          ['Living Rent-Free',         `First-time buyers: max DTI ${rentFree.maxDTI} unless ${factorWord(rentFree.compFactors)}`],
          ['Non-Occupant Co-Borrower', `Max DTI ${pctOf(income.nonOccupantCoBorrowerMaxDTI)} combined; occupying borrower must document ${pctOf(income.occupyingBorrowerMinIncomePctOfPITIA)} of PITIA`],
          ['Gift Funds',               `All or part allowed; ${matrix?.fundsToClose?.gifts?.itinNote}`],
          ['LTV Basis',                `${matrix?.ltvBasis}`],
          ['Prepayment Penalty',       'Prohibited'],
          ['Escrow',                   matrix?.insurance?.escrowRequired ? 'Required on all loans' : 'Not required'],
        ]} />
        <ReqCard title="Credit & Tradelines" rows={[
          // ⚠️ PUBLISHED MINIMUM ONLY. The exception band is internal.
          ['Minimum Credit Score', String(matrix?.ficoFloor)],
          ...((matrix?.tradelines?.options || []).map((o, i) => [`Tradeline Option ${i + 1}`, o])),
          ['Mortgage Lates',   derogRuleText(matrix, 'Mortgage lates')],
          ['Collections',      derogRuleText(matrix, 'Collections / charge-offs (non-mortgage)')],
          ['Judgments',        derogRuleText(matrix, 'Judgments')],
          ['Tax Liens',        derogRuleText(matrix, 'Tax liens')],
        ]} />
      </div>

      {/* 🔑 THE EXTENUATING PERIODS ARE IN THE TABLE, labelled as what they are — an underwriter path, never
          automatic. Rows from afmMatrix. */}
      <h3 style={{ margin: '28px 0 8px', fontSize: 16, fontWeight: 800, color: NEAR_BLACK }}>Derogatory Event Seasoning</h3>
      <DataTable headers={['Event', 'Rule']} rows={matrix?.derogSeasoning || []} />
      <p style={{ margin: '10px 0 0', fontSize: 12.5, color: '#6b7280', lineHeight: 1.6 }}>
        {derogRuleText(matrix, 'Extenuating circumstances')}
      </p>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 280px), 1fr))', gap: 14, marginTop: 22 }}>
        <ReqCard title="Income Documentation Types" rows={matrix?.altDoc || []}
          footer={`Not offered in this version: ${(matrix?.docTypesNotOffered || []).join(', ')}.`} />
        <ReqCard title="Reserve Requirements" rows={tiers.map((t) => [t.loan, `${t.reserves} PITIA`])}
          footer={matrix?.reserveOverlayFooter} />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 280px), 1fr))', gap: 14, marginTop: 22 }}>
        <ReqCard title="Appraisal" rows={[
          [`Up to ${usdOf(matrix?.appraisal?.deskReviewOver)}`, 'One full interior appraisal'],
          [`Over ${usdOf(matrix?.appraisal?.deskReviewOver)}`, 'Plus a desk review'],
          [`Over ${usdOf(matrix?.appraisal?.secondAppraisalOver)}`, 'Two independent appraisals; the lower value is used'],
        ]} />
        <ReqCard title="Compensating Factors" rows={(matrix?.compFactorList || []).map((f) => [f.label, f.threshold])}
          footer={`Exceptions need at least ${factorWord(matrix?.exceptionCompFactorsMin)}, decided by the credit committee.`} />
      </div>

      {/* PMI Requirements card + Coverage Matrix removed — non-QM loans
          can't bind MI; National MI integration was deleted in the MI
          removal pass. */}

      <h3 style={{ margin: '28px 0 8px', fontSize: 16, fontWeight: 800, color: NEAR_BLACK }}>AFM™ Rate &amp; Payment Simulator</h3>
      <p style={{ margin: '0 0 14px', fontSize: 13, color: '#6b7280' }}>
        Estimate the rate and monthly payment for a Doorly AFM™ loan based on loan parameters. For indicative purposes only — actual rates subject to credit approval.
      </p>
      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: 22 }}>
        <RatePricingSimulator />
      </div>

      {/* ⚠️ THE COLLAPSED "TIER REFERENCE" TABLE IS REMOVED, AND ITS FAILURE IS
          THE LESSON. It was added so a reviewer was "one click away from the
          canonical source so the matrix above can never silently drift" — a
          SECOND table beside the first, hidden behind a disclosure triangle.
          The visible matrix drifted anyway (FICO 560 against the engine's 550),
          because putting the right answer next to the wrong one does not make
          the wrong one go away; it just means both are published. The matrix
          above now IS the canonical source, so there is nothing to compare it
          against. */}
    </div>
  );
}

// ─── Tab 3: About Doorly ─────────────────────────────────────────────
// 🔑 THE ABOUT AND PROJECTIONS MATERIAL IS SERVED, NOT BUNDLED (Jesse, 2026-10-21): GET /api/uw-matrix/portal-confidential
// answers staff and NDA-signed approved investors only (backend/constants/portalConfidential.js). Partners see a note.
function ConfidentialGate({ children }) {
  const { content, status, code } = usePortalConfidential();
  if (status === 200 && content) return children(content);
  if (status == null) return <p style={{ fontSize: 14, color: '#6b7280' }}>Loading…</p>;
  if (code === 'NDA_REQUIRED') return <NdaPrompt />;
  return (
    <p data-testid="uw-confidential-restricted" style={{ fontSize: 14, color: '#374151' }}>
      This section is available to Doorly staff and to approved investors under NDA.
    </p>
  );
}

function AboutTab() {
  return (
    <ConfidentialGate>{(c) => (
      <div>
        <SectionHeader>Lending Philosophy</SectionHeader>
        {c.about.philosophy.map((t, i) => <Prose key={i}>{t}</Prose>)}

        <SectionHeader>The Doorly Model</SectionHeader>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 200px), 1fr))', gap: 14, marginBottom: 22 }}>
          {c.about.model.map((m) => <FlowStep key={m.n} n={m.n} title={m.title} body={m.body} />)}
        </div>

        <SectionHeader>Leadership</SectionHeader>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 260px), 1fr))', gap: 14, marginBottom: 16 }}>
          {c.about.leadership.map((p) => <PersonCard key={p.name} name={p.name} title={p.title} license={p.license} body={p.body} />)}
        </div>

        <h3 style={{ margin: '24px 0 10px', fontSize: 14, fontWeight: 700, color: NEAR_BLACK }}>Advisory Board</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 260px), 1fr))', gap: 14 }}>
          {c.about.advisors.map((p) => <PersonCard key={p.name} name={p.name} title={p.title} body={p.body} />)}
        </div>

        <div style={{ marginTop: 48 }}>
          <SectionHeader>Infrastructure &amp; Compliance Stack</SectionHeader>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 240px), 1fr))', gap: 14 }}>
          {c.about.stack.map((x) => <PartnerCard key={x.title} iconName={x.icon} title={x.title} body={x.body} />)}
        </div>
      </div>
    )}</ConfidentialGate>
  );
}

// ─── Tab 4: Projections ──────────────────────────────────────────────
function ProjectionsTab() {
  return (
    <ConfidentialGate>{(c) => (
      <div>
        <SectionHeader>Origination Projections</SectionHeader>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 200px), 1fr))', gap: 12, marginBottom: 18 }}>
          {c.projections.kpis.map((k) => <KpiTile key={k.label} label={k.label} value={k.value} />)}
        </div>

        <h3 style={{ margin: '8px 0 8px', fontSize: 16, fontWeight: 800, color: NEAR_BLACK }}>{c.projections.originationTitle}</h3>
        <DataTable headers={c.projections.originationTable.headers} rows={c.projections.originationTable.rows} />
        <p style={{ margin: '10px 0 24px', fontSize: 11, color: '#6b7280', lineHeight: 1.6 }}>{c.projections.disclaimer}</p>

        <SectionHeader>Capital Structure</SectionHeader>
        <ReqCard rows={c.projections.capitalStructure} />

        <div style={{ marginTop: 36 }}>
          <SectionHeader>Secondary Market</SectionHeader>
          <Prose>{c.projections.secondaryMarket}</Prose>
        </div>
      </div>
    )}</ConfidentialGate>
  );
}

// ─── Tab 5: Resources ────────────────────────────────────────────────
function ResourcesTab() {
  const [requestDoc, setRequestDoc] = useState(null);
  const downloads = [
    // 🔑 AFM v2, approved by Hannah 2026-10-21 — the PDF itself (docs/policy/doorly-afm-v2.pdf, served by the API).
    { title: 'Doorly AFM™ Underwriting Guidelines (PDF)', body: 'The AFM v2 guidelines, with the program matrix and exhibits.', pdf: true },
    ...(SAMPLE_TAPE_PUBLISHED ? [{ title: 'Sample Loan Tape',  body: `${SAMPLE_LOAN_TAPE.length}-loan illustrative AFM™ tape — loan IDs, addresses and borrower data are sample data, not closed originations.`, href: SAMPLE_TAPE_PATH }] : []),
    { title: 'Warehouse Line Package',                      body: 'Cover letter, balance sheet, and capital structure.', href: '#' },
  ];

  return (
    <div>
      <SectionHeader>Download Center</SectionHeader>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 240px), 1fr))', gap: 14, marginBottom: 22 }}>
        {downloads.map((d, i) => (
          <DownloadCard key={i} {...d} onRequestAccess={() => setRequestDoc(d.title)} />
        ))}
      </div>

      {requestDoc && <RequestAccessModal documentTitle={requestDoc} onClose={() => setRequestDoc(null)} />}

      <SectionHeader>Compliance &amp; Regulatory</SectionHeader>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 240px), 1fr))', gap: 14, marginBottom: 22 }}>
        <ComplianceCard iconName="document"     title="TRID Compliance"     body="All Doorly AFM™ loans are originated in compliance with TRID (TILA-RESPA Integrated Disclosure) requirements, including Loan Estimate and Closing Disclosure delivery timelines." />
        <ComplianceCard iconName="check-circle" title="ATR / QM Status"     body="Doorly AFM™ loans are originated under the Ability-to-Repay (ATR) standard as non-QM loans. Each loan file includes documented income, employment, assets, and a good-faith ATR determination per Regulation Z." />
        <ComplianceCard iconName="scale"        title="ECOA / Fair Lending" body="Doorly underwrites all applications in compliance with the Equal Credit Opportunity Act (ECOA) and Fair Housing Act. Adverse action notices are issued in compliance with ECOA and FCRA requirements." />
        <ComplianceCard iconName="building"     title="State Licensing"     body="">
         <div className="scroll-x">
          <table style={{ width: '100%', minWidth: 360, borderCollapse: 'collapse', fontSize: 13, marginTop: 4 }}>
            <thead>
              <tr>
                <th style={{ textAlign: 'left', padding: '6px 8px 6px 0', color: '#6b7280', fontWeight: 700, fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.04 }}>State</th>
                <th style={{ textAlign: 'left', padding: '6px 8px', color: '#6b7280', fontWeight: 700, fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.04 }}>License Type</th>
                <th style={{ textAlign: 'left', padding: '6px 0', color: '#6b7280', fontWeight: 700, fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.04 }}>License Number</th>
              </tr>
            </thead>
            <tbody>
              {/* 🔑 RENDERED FROM THE LICENCE TABLE, NEVER TYPED (Hannah, 2026-11-03).
                  Both rows used to be literals here and on the investor page;
                  Florida read "pending" only because both pages had been edited by
                  hand. LICENSES is the frontend mirror of backend/constants/
                  marketExpansion.js LICENSES, held identical by backend/test/
                  licensingClaimSource.test.js — which also fails on a state name typed
                  into a licence table. A pending row prints no licence number: a number
                  implies an issued licence. */}
              {LICENSES.map((l) => (
                <tr key={l.code} style={{ borderTop: '1px solid #f3f4f6' }}>
                  <td style={{ padding: '6px 8px 6px 0', color: NEAR_BLACK }}>{l.state}</td>
                  <td style={{ padding: '6px 8px', color: '#374151' }}>
                    {l.type}{l.status === 'issued' ? '' : <> — <strong>application pending</strong></>}
                  </td>
                  <td style={{ padding: '6px 0', color: l.status === 'issued' ? '#374151' : '#9ca3af' }}>
                    {l.status === 'issued' ? `NMLS #${l.nmls}` : 'Not yet issued'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
         </div>
        </ComplianceCard>
      </div>

      <SectionHeader>Contact</SectionHeader>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 260px), 1fr))', gap: 14 }}>
        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: 22 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: PRIMARY, textTransform: 'uppercase', letterSpacing: 0.06, marginBottom: 8 }}>
            Warehouse &amp; Secondary Market Inquiries
          </div>
          <div style={{ fontSize: 16, fontWeight: 800, color: NEAR_BLACK }}>Jesse Coody</div>
          <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 8 }}>Founder &amp; CEO</div>
          <div style={{ fontSize: 13, color: '#374151', lineHeight: 1.8 }}>
            <a href="mailto:jesse@godoorly.com" style={{ color: PRIMARY, textDecoration: 'none', fontWeight: 600 }}>jesse@godoorly.com</a><br />
            <a href="https://godoorly.com" target="_blank" rel="noopener noreferrer" style={{ color: PRIMARY, textDecoration: 'none' }}>godoorly.com</a>
          </div>
        </div>
        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: 22 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: PRIMARY, textTransform: 'uppercase', letterSpacing: 0.06, marginBottom: 8 }}>
            General Inquiries
          </div>
          <div style={{ fontSize: 14, color: '#374151', lineHeight: 1.8 }}>
            Go Doorly, LLC<br />
            8708 S Congress Ave, Ste E500-202<br />
            Austin, TX 78745<br />
            NMLS #2764749<br />
            <a href="mailto:support@godoorly.com" style={{ color: PRIMARY, textDecoration: 'none', fontWeight: 600 }}>support@godoorly.com</a>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Shared atoms ────────────────────────────────────────────────────
const SectionHeader = ({ children }) => (
  <h2 style={{
    margin: '0 0 12px',
    fontSize: 18, fontWeight: 800, color: NEAR_BLACK,
    paddingBottom: 8, borderBottom: `2px solid ${PRIMARY}`,
  }}>
    {children}
  </h2>
);

const Prose = ({ children }) => (
  <p style={{ margin: '0 0 12px', fontSize: 14, lineHeight: 1.7, color: '#374151' }}>
    {children}
  </p>
);

function DataTable({ headers, rows }) {
  // Width-aware: tables with many columns need a wider min-width so the
  // user can scroll instead of seeing a collapsed mess on mobile. The
  // 600px floor matches the scroll-x recipe in index.css.
  const minTableWidth = headers.length > 4 ? 720 : headers.length > 2 ? 480 : 360;
  return (
    <>
      <div className="scroll-x" style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12 }}>
        <table style={{ width: '100%', minWidth: minTableWidth, borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr>
              {headers.map((h, i) => (
                <th key={i} style={{
                  background: NEAR_BLACK, color: '#fff',
                  padding: '10px 12px', textAlign: 'left',
                  fontWeight: 700, fontSize: 11,
                  textTransform: 'uppercase', letterSpacing: 0.04,
                  whiteSpace: 'nowrap',
                }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} style={{ background: i % 2 === 0 ? '#ffffff' : '#f9fafb', borderTop: '1px solid #e5e7eb' }}>
                {r.map((c, j) => (
                  <td key={j} style={{ padding: '10px 12px', color: '#374151' }}>{c}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="scroll-hint">← Scroll to see more →</div>
    </>
  );
}

function ReqCard({ title, rows, footer, style }) {
  return (
    <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: 18, ...style }}>
      {title && (
        <div style={{ fontSize: 12, fontWeight: 700, color: PRIMARY, textTransform: 'uppercase', letterSpacing: 0.06, marginBottom: 10 }}>
          {title}
        </div>
      )}
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
        <tbody>
          {rows.map(([k, v], i) => (
            <tr key={i} style={{ borderTop: i === 0 ? 'none' : '1px solid #f3f4f6' }}>
              <td style={{ padding: '8px 8px 8px 0', color: '#6b7280', fontWeight: 600, width: '40%', verticalAlign: 'top' }}>{k}</td>
              <td style={{ padding: '8px 0', color: NEAR_BLACK }}>{v}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {footer && (
        <div style={{ marginTop: 10, padding: '10px 12px', background: '#fffde4', border: '1px solid #f9e9a0', borderRadius: 8, fontSize: 12, color: '#374151', lineHeight: 1.6 }}>
          {footer}
        </div>
      )}
    </div>
  );
}

const KpiTile = ({ label, value }) => (
  <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: '14px 16px' }}>
    <div style={{ fontSize: 11, color: '#6b7280', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>{label}</div>
    <div style={{ fontSize: 18, fontWeight: 800, color: NEAR_BLACK }}>{value}</div>
  </div>
);

const FlowStep = ({ n, title, body }) => (
  <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: 18 }}>
    <div style={{ width: 32, height: 32, borderRadius: 999, background: PRIMARY, color: '#fff', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 800, marginBottom: 10 }}>
      {n}
    </div>
    <div style={{ fontSize: 15, fontWeight: 800, color: NEAR_BLACK, marginBottom: 6 }}>{title}</div>
    <div style={{ fontSize: 13, color: '#6b7280', lineHeight: 1.6 }}>{body}</div>
  </div>
);

const PersonCard = ({ name, title, license, body }) => (
  <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: 22 }}>
    <div style={{ fontSize: 17, fontWeight: 800, color: NEAR_BLACK }}>{name}</div>
    <div style={{ fontSize: 12, color: PRIMARY, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.06, marginTop: 2 }}>{title}</div>
    {license && (
      <div style={{ fontSize: 11, color: '#6b7280', fontWeight: 600, marginTop: 4, marginBottom: 10 }}>{license}</div>
    )}
    {!license && <div style={{ marginBottom: 10 }} />}
    <div style={{ fontSize: 13, color: '#374151', lineHeight: 1.7 }}>{body}</div>
  </div>
);

const PartnerCard = ({ iconName, title, body }) => (
  <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: 18, display: 'flex', gap: 12 }}>
    <span style={{ width: 36, height: 36, borderRadius: 8, background: '#eff3fe', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
      <Icon name={iconName} size={18} color={PRIMARY} />
    </span>
    <div>
      <div style={{ fontSize: 14, fontWeight: 800, color: NEAR_BLACK }}>{title}</div>
      <div style={{ fontSize: 13, color: '#6b7280', lineHeight: 1.6, marginTop: 2 }}>{body}</div>
    </div>
  </div>
);

function ComplianceCard({ iconName, title, body, children }) {
  return (
    <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: 18 }}>
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 8 }}>
        <span style={{ width: 36, height: 36, borderRadius: 8, background: '#eff3fe', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <Icon name={iconName} size={18} color={PRIMARY} />
        </span>
        <div style={{ fontSize: 14, fontWeight: 800, color: NEAR_BLACK }}>{title}</div>
      </div>
      {body && <div style={{ fontSize: 13, color: '#6b7280', lineHeight: 1.7 }}>{body}</div>}
      {children}
    </div>
  );
}

function DownloadCard({ title, body, href, pdf, onRequestAccess }) {
  // Sample tape and the guidelines PDF are live links; everything else opens the
  // RequestAccessModal so we capture the requester's contact details +
  // their specific document interest, route to investors@godoorly.com.
  const isSampleTape = href === SAMPLE_TAPE_PATH;
  // The guidelines PDF is fetched with the viewer's session (it is not a public file), then opened.
  const isFile = !!pdf;
  const inner = (
    <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: 18, height: '100%', display: 'flex', flexDirection: 'column' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
        <span style={{ width: 32, height: 32, borderRadius: 8, background: '#eff3fe', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <Icon name="document" size={16} color={PRIMARY} />
        </span>
        <div style={{ fontSize: 14, fontWeight: 800, color: NEAR_BLACK }}>{title}</div>
      </div>
      <div style={{ fontSize: 13, color: '#6b7280', lineHeight: 1.6, flex: 1, marginBottom: 12 }}>{body}</div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <span style={{ display: 'inline-block', padding: '6px 12px', borderRadius: 8, background: PRIMARY, color: '#fff', fontSize: 12, fontWeight: 700 }}>
          {isSampleTape ? 'View Sample Tape →' : isFile ? 'Download PDF →' : 'Request Access →'}
        </span>
      </div>
    </div>
  );

  if (isSampleTape) {
    return <Link to={href} style={{ textDecoration: 'none' }}>{inner}</Link>;
  }
  if (isFile) {
    return <button type="button" data-testid="uw-afm-pdf-link" onClick={() => openAfmPdf()}
      style={{ textDecoration: 'none', background: 'none', border: 0, padding: 0, textAlign: 'left', cursor: 'pointer', width: '100%' }}>{inner}</button>;
  }
  return (
    <button type="button" onClick={onRequestAccess}
      style={{ textAlign: 'left', background: 'none', border: 'none', padding: 0, cursor: 'pointer', display: 'block', width: '100%' }}>
      {inner}
    </button>
  );
}

// ─── Request Access Modal ────────────────────────────────────────────
// Submits to POST /api/market/underwriting/request-access which fans
// out to investors@godoorly.com via sendMarketAlertEmail.
function RequestAccessModal({ documentTitle, onClose }) {
  const [form, setForm] = useState({ name: '', email: '', firm: '', phone: '', message: '' });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      setError('Name and email are required.');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      await axios.post(`${API_URL}/api/market/underwriting/request-access`, {
        ...form,
        document: documentTitle,
      });
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to submit. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const labelStyle = { fontSize: 12, fontWeight: 700, color: '#374151', marginBottom: 6, display: 'block' };
  const inputStyle = { width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #e5e7eb', fontSize: 14, color: NEAR_BLACK, boxSizing: 'border-box', outline: 'none' };

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.6)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: '#fff', borderRadius: 14, maxWidth: 540, width: '100%', maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
        <div style={{ padding: '18px 24px', borderBottom: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <div style={{ fontSize: 17, fontWeight: 800, color: NEAR_BLACK }}>Request Access</div>
            <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>{documentTitle}</div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 20, color: '#9ca3af' }} aria-label="Close">✕</button>
        </div>

        {success ? (
          <div style={{ padding: '40px 28px', textAlign: 'center' }}>
            <div style={{ width: 56, height: 56, margin: '0 auto 14px', borderRadius: 12, background: '#f0fde4', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
              <Icon name="check-circle" size={28} color={NEAR_BLACK} />
            </div>
            <div style={{ fontSize: 16, fontWeight: 800, color: NEAR_BLACK, marginBottom: 6 }}>Request received</div>
            <div style={{ fontSize: 13, color: '#6b7280', lineHeight: 1.6, marginBottom: 18 }}>
              Thank you. The Doorly team will follow up with the requested materials shortly.
            </div>
            <button onClick={onClose} style={{ padding: '9px 18px', background: PRIMARY, border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 700, color: '#fff' }}>
              Close
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} style={{ padding: '20px 24px', overflowY: 'auto' }}>
            <div className="stack-mobile" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
              <div>
                <label style={labelStyle}>Full Name <span style={{ color: '#ff082f' }}>*</span></label>
                <input value={form.name} onChange={update('name')} required style={inputStyle}
                  onFocus={(e) => (e.target.style.borderColor = PRIMARY)}
                  onBlur={(e) => (e.target.style.borderColor = '#e5e7eb')} />
              </div>
              <div>
                <label style={labelStyle}>Email <span style={{ color: '#ff082f' }}>*</span></label>
                <input type="email" value={form.email} onChange={update('email')} required style={inputStyle}
                  onFocus={(e) => (e.target.style.borderColor = PRIMARY)}
                  onBlur={(e) => (e.target.style.borderColor = '#e5e7eb')} />
              </div>
              <div>
                <label style={labelStyle}>Firm / Company</label>
                <input value={form.firm} onChange={update('firm')} style={inputStyle}
                  onFocus={(e) => (e.target.style.borderColor = PRIMARY)}
                  onBlur={(e) => (e.target.style.borderColor = '#e5e7eb')} />
              </div>
              <div>
                <label style={labelStyle}>Phone</label>
                <input value={form.phone} onChange={update('phone')} style={inputStyle}
                  onFocus={(e) => (e.target.style.borderColor = PRIMARY)}
                  onBlur={(e) => (e.target.style.borderColor = '#e5e7eb')} />
              </div>
            </div>
            <div style={{ marginBottom: 12 }}>
              <label style={labelStyle}>Message (optional)</label>
              <textarea value={form.message} onChange={update('message')} rows={4}
                placeholder="Anything we should know about your interest, timeline, or capital plans?"
                style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit', lineHeight: 1.6 }}
                onFocus={(e) => (e.target.style.borderColor = PRIMARY)}
                onBlur={(e) => (e.target.style.borderColor = '#e5e7eb')} />
            </div>
            <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 14 }}>
              Your request will be sent to the Doorly team at investors@godoorly.com. We'll respond within 1 business day.
            </div>
            {error && (
              <div style={{ marginBottom: 12, padding: '10px 12px', background: '#fff5f6', border: '1px solid #ffd6db', borderRadius: 8, color: '#ff082f', fontSize: 13 }}>
                {error}
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button type="button" onClick={onClose} disabled={submitting}
                style={{ padding: '9px 16px', background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600, color: '#374151' }}>
                Cancel
              </button>
              <button type="submit" disabled={submitting || !form.name.trim() || !form.email.trim()}
                style={{ padding: '9px 18px', background: PRIMARY, border: 'none', borderRadius: 8, cursor: submitting ? 'not-allowed' : 'pointer', fontSize: 13, fontWeight: 700, color: '#fff', opacity: submitting || !form.name.trim() || !form.email.trim() ? 0.5 : 1 }}>
                {submitting ? 'Submitting…' : 'Send Request'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
